/** Languages offered by `app-language-select` — today the agent's default
 *  language (template setting `default_language`, migration 198).
 *
 *  Codes are ISO 639-1; the list mirrors `_LANGUAGE_NAMES` in
 *  al-tutor/app/agents/shared/lang.py — the languages the language lock
 *  names natively. The name in the portal's own language comes from
 *  `Intl.DisplayNames` at runtime, so only the native name is kept here. */
export interface AgentLanguage {
  code: string;
  native: string;
}

export const AGENT_LANGUAGES: AgentLanguage[] = [
  { code: 'af', native: 'Afrikaans' },
  { code: 'ar', native: 'العربية' },
  { code: 'bg', native: 'Български' },
  { code: 'bn', native: 'বাংলা' },
  { code: 'ca', native: 'Català' },
  { code: 'cs', native: 'Čeština' },
  { code: 'cy', native: 'Cymraeg' },
  { code: 'da', native: 'Dansk' },
  { code: 'de', native: 'Deutsch' },
  { code: 'el', native: 'Ελληνικά' },
  { code: 'en', native: 'English' },
  { code: 'es', native: 'Español' },
  { code: 'et', native: 'Eesti' },
  { code: 'fa', native: 'فارسی' },
  { code: 'fi', native: 'Suomi' },
  { code: 'fr', native: 'Français' },
  { code: 'gu', native: 'ગુજરાતી' },
  { code: 'he', native: 'עברית' },
  { code: 'hi', native: 'हिन्दी' },
  { code: 'hr', native: 'Hrvatski' },
  { code: 'hu', native: 'Magyar' },
  { code: 'id', native: 'Bahasa Indonesia' },
  { code: 'it', native: 'Italiano' },
  { code: 'ja', native: '日本語' },
  { code: 'kn', native: 'ಕನ್ನಡ' },
  { code: 'ko', native: '한국어' },
  { code: 'lt', native: 'Lietuvių' },
  { code: 'lv', native: 'Latviešu' },
  { code: 'mk', native: 'Македонски' },
  { code: 'ml', native: 'മലയാളം' },
  { code: 'mr', native: 'मराठी' },
  { code: 'ne', native: 'नेपाली' },
  { code: 'nl', native: 'Nederlands' },
  { code: 'no', native: 'Norsk' },
  { code: 'pa', native: 'ਪੰਜਾਬੀ' },
  { code: 'pl', native: 'Polski' },
  { code: 'pt', native: 'Português' },
  { code: 'ro', native: 'Română' },
  { code: 'ru', native: 'Русский' },
  { code: 'sk', native: 'Slovenčina' },
  { code: 'sl', native: 'Slovenščina' },
  { code: 'so', native: 'Soomaali' },
  { code: 'sq', native: 'Shqip' },
  { code: 'sv', native: 'Svenska' },
  { code: 'sw', native: 'Kiswahili' },
  { code: 'ta', native: 'தமிழ்' },
  { code: 'te', native: 'తెలుగు' },
  { code: 'th', native: 'ไทย' },
  { code: 'tl', native: 'Tagalog' },
  { code: 'tr', native: 'Türkçe' },
  { code: 'uk', native: 'Українська' },
  { code: 'ur', native: 'اردو' },
  { code: 'vi', native: 'Tiếng Việt' },
  { code: 'zh', native: '中文' },
];

export interface AgentLanguageOption extends AgentLanguage {
  /** What the field shows: "Türkisch — Türkçe (tr)". */
  label: string;
  /** Lower-cased haystack for the type-to-find filter. */
  search: string;
}

/** Options labelled in `uiLang`, sorted by that label. The English name is
 *  always searchable too — "turk" finds Türkçe in a German portal. */
export function agentLanguageOptions(uiLang: string): AgentLanguageOption[] {
  const names = displayNames(uiLang);
  const english = displayNames('en');
  return AGENT_LANGUAGES.map((l) => {
    const local = names?.of(l.code) || l.native;
    const title =
      local.toLowerCase() === l.native.toLowerCase()
        ? l.native
        : `${local} — ${l.native}`;
    return {
      ...l,
      label: `${title} (${l.code})`,
      search: [local, l.native, english?.of(l.code) || '', l.code]
        .join(' ')
        .toLowerCase(),
    };
  }).sort((a, b) => a.label.localeCompare(b.label, uiLang));
}

function displayNames(lang: string): Intl.DisplayNames | undefined {
  try {
    return new Intl.DisplayNames([lang], { type: 'language' });
  } catch {
    return undefined;
  }
}
